import React, { useState, useEffect, CSSProperties } from "react";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import "jquery-confirm/css/jquery-confirm.css";
import "jquery-confirm";
import { truncateMiddleString } from "../../../../utils/stringUtils";

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  onFileAction?: (action: string, row: any) => void;
  onDownloadAction?: (rowId: any, key: string, forcedValue?: number) => void;
  on110DownloadAction?: (action: string, row: any) => void;
  isLogTable?: boolean;

  onRowSelect?: (row: any) => void;
  selectedRow?: any;
  selectedRowRadio?: any;
  isCheckbox?: boolean;
  onCheckboxSelect?: (selectedRows: any[]) => void;
  selectedRowIds?: any;
}

const PartyNotificationDataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onSortAction,
  onFileAction,
  onDownloadAction,
  on110DownloadAction,
  isLogTable = false,
  onRowSelect,
  selectedRow,
  isCheckbox = false,
  onCheckboxSelect,
  selectedRowIds = [],
}) => {
  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [instructionOption, setInstructionOption] = useState<
    { value: string; label: string }[]
  >([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ASC" | "DESC" | null;
  }>({
    key: "",
    direction: null,
  });

  const tableStyle = {
    border: "1px solid #ddd",
    borderCollapse: "collapse" as const,
    width: "100%",
    tableLayout: "fixed",
  };

  const thStyle: CSSProperties = {
    border: "none",
    padding: "8px",
    backgroundColor: "#e0f7fa",
    color: "#000",
    cursor: "pointer",
    position: "sticky",
    top: 0,
    zIndex: -90,
  };

  const tdStyle = {
    border: "none",
    padding: "8px",
  };

  const handleSort = (key: string, direction: "ASC" | "DESC") => {
    if (sortConfig.key === key && sortConfig.direction === direction) {
      return;
    }
    const sorted = [...sortedRows].sort((a, b) => {
      if (direction === "ASC") {
        return a[key] > b[key] ? 1 : -1;
      }
      if (direction === "DESC") {
        return a[key] < b[key] ? 1 : -1;
      }
      return 0;
    });

    setSortedRows(sorted);
    setSortConfig({ key, direction });
    onSortAction?.({ key, direction });
  };

  useEffect(() => {
    if (
      isCheckbox &&
      rows?.length > 0 &&
      selectedRowIds?.length > 0 &&
      Array.isArray(selectedRowIds)
    ) {
      const cleanedIds = selectedRowIds?.map((id: string) => id?.trim());
      const selectedIdSet = new Set(cleanedIds);

      const selected = rows?.filter((row) => selectedIdSet.has(String(row.id)));

      const unselected = rows?.filter(
        (row) => !selectedIdSet.has(String(row.id)),
      );

      const ordered = [...selected, ...unselected];

      setSortedRows(ordered);
      setSelectedRows(selected);
      const allSelected = rows?.length > 0 && selected?.length === rows?.length;
      setSelectAll(allSelected);
    } else {
      setSortedRows(rows || []);
      setSelectedRows([]);
    }
  }, [rows]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    const updated = newSelectAll ? [...sortedRows] : [];
    setSelectedRows(updated);
    setSelectAll(newSelectAll);
    onCheckboxSelect?.(updated);
  };

  const handleColumnSelectAll = (key: string) => {
    if (!sortedRows || sortedRows.length === 0) return;

    const allSelected = sortedRows.every((row) => row[key] === 1);

    const newValue = allSelected ? 0 : 1;

    sortedRows.forEach((row) => {
      onDownloadAction?.(row.id, key, newValue);
    });
  };

  return (
    <>
      <div className="table-responsive border-0 mb-2">
        <Table style={{ tableLayout: "fixed" }}>
          <thead>
            <tr>
              {isCheckbox && (
                <th style={{ width: "5%", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
              )}

              {headers.map((header) => (
                <th
                  key={header.key}
                  style={{
                    border: "none",
                    padding: "8px",
                    backgroundColor: "#e0f7fa",
                    color: "#000",
                    cursor: header.sortable ? "pointer" : "default",
                    position: "sticky",
                    top: 0,
                    zIndex: -90,

                    textAlign: [
                      "isActiveDetails",
                      "appointmentStatusDesc",
                      "actions",
                      "phonenumber",
                      "download",
                      "downloadFile",
                    ].includes(header.key)
                      ? "center"
                      : "left",
                    ...header.style,
                  }}
                >
                  <div
                    style={{
                      display: [
                        "isActiveDetails",
                        "appointmentStatusDesc",
                        "actions",
                        "phonenumber",
                        "contactphonenumber",
                        "download",
                        "downloadFile",
                      ].includes(header.key)
                        ? "flex"
                        : "flex",

                      justifyContent: [
                        "isActiveDetails",
                        "appointmentStatusDesc",
                        "actions",
                        "phonenumber",
                        "contactphonenumber",
                        "download",
                        "downloads",
                        "downloadFile",
                      ].includes(header.key)
                        ? "space-evenly"
                        : "space-between",

                      alignItems: "center",
                    }}
                  >
                    {header.key === "isEmailNotification" ||
                    header.key === "isSmsNotification" ||
                    header.key === "isInAppNotification" ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            sortedRows.length > 0 &&
                            sortedRows.every((row) => row[header.key] === 1)
                          }
                          onChange={() => handleColumnSelectAll(header.key)}
                        />
                        <span>{header.label}</span>
                      </div>
                    ) : (
                      <span>{header.label}</span>
                    )}
                    {header.sortable && (
                      <span
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          marginLeft: "4px",
                          cursor: "pointer",
                          position: "absolute",
                          right: "0px",
                        }}
                      >
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === "ASC"
                        ) && (
                          <span
                            className="material-icons"
                            style={{
                              fontSize: "18px",
                              lineHeight: "10px",
                            }}
                            onClick={() => handleSort(header.key, "ASC")}
                          >
                            arrow_drop_up
                          </span>
                        )}
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === "DESC"
                        ) && (
                          <span
                            className="material-icons"
                            style={{
                              fontSize: "18px",
                              lineHeight: "10px",
                            }}
                            onClick={() => handleSort(header.key, "DESC")}
                          >
                            arrow_drop_down
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows?.length > 0 ? (
              sortedRows.map((row, index) => (
                <tr
                  key={row.id}
                  style={{
                    backgroundColor: index % 2 === 1 ? "white" : "#f2f2f2",
                  }}
                >
                  {headers.map((header) => {
                    if (
                      header.key === "isEmailNotification" ||
                      header.key === "isSmsNotification" ||
                      header.key === "isInAppNotification"
                    ) {
                      return (
                        <td
                          key={header.key}
                          style={{ padding: "8px", textAlign: "center" }}
                        >
                          <input
                            type="checkbox"
                            checked={row[header.key] === 1}
                            onChange={() =>
                              onDownloadAction?.(row.id, header.key)
                            }
                          />
                        </td>
                      );
                    }

                    if (header.key === "actions") {
                      return (
                        <td
                          key={header.key}
                          style={{ padding: "8px", textAlign: "center" }}
                        >
                          {row.actions?.View && (
                            <OverlayTrigger
                              placement="auto"
                              delay={{ show: 200, hide: 10 }}
                              overlay={
                                <Tooltip
                                  id={`view-tooltip-${row.id}`}
                                  style={{ backgroundColor: "#b3e0ff" }}
                                >
                                  View
                                </Tooltip>
                              }
                            >
                              <span
                                className="material-icons color_brand"
                                onClick={() => onAction?.(row, "View")}
                                style={{ cursor: "pointer" }}
                              >
                                visibility
                              </span>
                            </OverlayTrigger>
                          )}
                          {row.actions?.Edit && (
                            <OverlayTrigger
                              placement="auto"
                              delay={{ show: 200, hide: 10 }}
                              overlay={
                                <Tooltip
                                  id={`edit-tooltip-${row.id}`}
                                  style={{ backgroundColor: "#b3e0ff" }}
                                >
                                  Edit
                                </Tooltip>
                              }
                            >
                              <span
                                className="material-icons color_brand"
                                onClick={() => onAction?.(row, "Edit")}
                                style={{ cursor: "pointer" }}
                              >
                                edit
                              </span>
                            </OverlayTrigger>
                          )}
                        </td>
                      );
                    }

                    const cellContent = row[header.key];
                    return (
                      <td
                        key={header.key}
                        style={{
                          padding: header.isNumeric
                            ? "8px 20px 8px 8px !important"
                            : "8px",
                          textAlign: header.isNumeric ? "center" : "left",
                        }}
                      >
                        <OverlayTrigger
                          placement="auto"
                          delay={{ show: 200, hide: 10 }}
                          overlay={
                            cellContent &&
                            cellContent?.length > (isLogTable ? 60 : 15) ? (
                              <Tooltip
                                id={`tooltip-${header.key}-${row.id}`}
                                style={{ backgroundColor: "#b3e0ff" }}
                              >
                                {cellContent}
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span
                            className={`d-inline-block ${
                              !isLogTable ? "text-truncate" : ""
                            }`}
                            style={{
                              maxWidth: isLogTable ? "none" : "100%",
                              cursor: isLogTable ? "default" : "pointer",
                            }}
                          >
                            {cellContent}
                          </span>
                        </OverlayTrigger>
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers?.length} style={{ textAlign: "center" }}>
                  <span className="no_rec_data">No Record Found"</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default PartyNotificationDataTable;
